import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import { useCheckoutState } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { getInitialDynamicFieldValues, useDynamicFields } from '../../utils/dynamicFields';
import useAmazonPay, { redirectToAmazon } from '../../utils/useAmazonPay';
import MainHeadline from '../atoms/MainHeadline';
import SubHeadline from '../atoms/SubHeadline';
import AddressOverview from '../molecules/AddressOverview';
import BillingAddress from '../molecules/BillingAddress';
import CarrierOptions from '../molecules/CarrierOptions';
import DynamicFieldArea from '../molecules/DynamicFieldArea';
import GiftCardList from '../molecules/GiftCardList';
import LoyaltyProgram from '../molecules/LoyaltyProgram';
import Navigation from '../molecules/Navigation';
import { showDefaultError } from '../molecules/Notifications';
import PaymentOptions from '../molecules/PaymentOptions';
import PaymentReviewButton from '../molecules/PaymentReview';
import PriceSummary from '../molecules/PriceSummary';
import VoucherList from '../molecules/VoucherList';
import ArvatoPayment from '../payment/ArvatoPayment';
import ComputopPayment from '../payment/ComputopPayment';
import { useKlarnaPayment } from '../payment/KlarnaPayment';

const PaymentStep = () => {
  const { checkoutState, confirmOrder, setPollingEnabled } = useCheckoutState();
  const navigate = useNavigate();
  const { submitDynamicFieldArea } = useDynamicFields();
  const [isLoading, setLoading] = useState(false);

  const {
    customer,
    orderState: { addresses, shipping, /* memberships, */ payment, basket },
    orderConfirmation,
  } = checkoutState;

  const isCollectionPoint = !!addresses?.shipping?.collectionPoint;
  const [isEditingBillingAddress, setEditingBillingAddress] = useState(isCollectionPoint && !addresses?.billing);

  const selectedCarrier = shipping?.selected?.[0];
  const selectedPaymentMethod = payment?.selected?.[0];
  const selectedGiftCards = payment?.giftCards;

  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false);
  useEffect(() => setPaymentModalOpen(!!orderConfirmation?.redirect), [orderConfirmation?.redirect]);

  const basketTotal = basket.total.costWithTaxWithVouchersWithServiceCostsWithGiftCard;
  const basketSubtotal = basket.total.costWithTaxWithoutAbsoluteVouchersWithoutServiceCosts;
  const basketShipping = basket.total.costShippingServiceCosts;

  const giftCardDiscount = selectedGiftCards?.[0]?.recurringPaymentInformation?.remainingAmount; // Showing first giftcard discount

  const { authorizeKlarnaPayment } = useKlarnaPayment();
  const { isAmazonSdkLoaded } = useAmazonPay([
    '#control-confirmation-billing',
    '#control-confirmation-shipping',
    '#amazon-link-billing',
    '#amazon-link-shipping',
    '#change-payment',
  ]);

  if (!customer) {
    return <Navigate to="/auth" replace />;
  } else if (!addresses?.shipping) {
    return <Navigate to="/shipping" replace />;
  }

  const { carrierKey, carrierGroupKey, shippingOptionKey } = selectedCarrier ?? {
    carrierKey: '',
    carrierGroupKey: '',
    shippingOptionKey: '',
  };

  const isDifferentAddress = !!addresses?.billing && addresses?.billing.hash !== addresses?.shipping?.hash;

  const initialValues = {
    carrier: { carrierKey, carrierGroupKey, shippingOptionKey },
    billingAddressType: isDifferentAddress || isCollectionPoint ? 'differentAddress' : 'sameAddress',
    paymentMethod: selectedPaymentMethod?.paymentOptionKey ?? selectedGiftCards?.[0].paymentOptionKey ?? '',
    isNewsletterSignUpAccepted: false,
    isTermsAndPrivacyAccepted: false,
    sepaDirectDebit: {
      iban: '',
      accountOwner: '',
    },
    creditCard: {
      issuer: '',
      number: '',
      holder: '',
      expiration: '',
      cvv: '',
    },
    adyenIdeal: {
      bank: '',
    },
    adyenCreditcard: {
      paymentMethod: {},
      browserInfo: {},
      isValid: false,
    },
    ...getInitialDynamicFieldValues(checkoutState, 'paymentOptions', 'payment'),
  };

  const onSubmit = async (values: typeof initialValues) => {
    setPollingEnabled(false);

    try {
      await submitDynamicFieldArea(
        {
          area: 'paymentOptions',
          conditionValues: {
            paymentOptionKey: values.paymentMethod,
          },
        },
        values
      );
      await submitDynamicFieldArea(
        {
          area: 'payment',
          conditionValues: {
            paymentOptionKey: values.paymentMethod,
            carrierGroupKey: values.carrier.carrierGroupKey,
            shippingOptionKey: values.carrier.shippingOptionKey,
          },
        },
        values
      );
      if (
        [
          'klarna_paylater',
          'klarna_paynow_direct_debit',
          'klarna_paynow_direct_bank_transfer',
          'klarna_installments',
        ].includes(values.paymentMethod)
      ) {
        await authorizeKlarnaPayment();
      } else if (values.paymentMethod === 'amazon_pay' && !!selectedPaymentMethod!.configuration?.initializationData) {
        redirectToAmazon(selectedPaymentMethod!.configuration?.initializationData);
        return;
      }
      const currentUrl = new URL(window.location.href);
      currentUrl.hash = '';
      await confirmOrder({
        paymentOptionKey: values.paymentMethod,
        currentUrl: currentUrl.toString(),
        specification:
          values.paymentMethod === 'ratepay_directdebit'
            ? { iban: values.sepaDirectDebit.iban }
            : values.paymentMethod === 'adyen_ideal'
            ? { key: values.adyenIdeal.bank }
            : values.paymentMethod === 'adyen_creditcard'
            ? { paymentMethod: values.adyenCreditcard.paymentMethod, browserInfo: values.adyenCreditcard.browserInfo }
            : undefined,
      });
    } catch (e) {
      console.error(e);
      showDefaultError();
      setPollingEnabled(true);
    }
  };

  const isNavigationDisabled = ({ paymentMethod, adyenCreditcard, adyenIdeal }: typeof initialValues) => {
    const isPaymentDataInvalid =
      (paymentMethod === 'adyen_creditcard' && !adyenCreditcard.isValid) ||
      (paymentMethod === 'adyen_ideal' && !adyenIdeal.bank);
    return isEditingBillingAddress || !paymentMethod || isPaymentDataInvalid;
  };

  const isLoyaltyProgramEnabled = config.features.loyaltyProgram.enabled;
  // && memberships?.suggestions?.some(
  //   ({ membershipTypeKey }) => membershipTypeKey === config.features.loyaltyProgram.type
  // );

  const areGiftCardsEnabled = config.features.giftCards.enabled;
  const areVouchersEnabled = config.features.vouchers.enabled;

  return (
    <>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {({ values }) => (
          <div className="form payment-form">
            <MainHeadline>
              <FormattedMessage id="payment.headline" />
            </MainHeadline>
            <AddressOverview addressType="shipping" onChangeClick={() => navigate('/shipping')} showTitle />
            {config.carrierOptions.position === 'payment' && !isCollectionPoint && (
              <CarrierOptions setLoading={setLoading} />
            )}
            <BillingAddress
              isCollectionPoint={isCollectionPoint}
              isEditingBillingAddress={isEditingBillingAddress}
              setEditingBillingAddress={setEditingBillingAddress}
              setLoading={setLoading}
            />
            {isAmazonSdkLoaded ? (
              <PaymentReviewButton paymentMethod="amazon_pay" />
            ) : (
              <PaymentOptions setLoading={setLoading} />
            )}
            {isLoyaltyProgramEnabled && <LoyaltyProgram />}
            {(areGiftCardsEnabled || areVouchersEnabled) && (
              <SubHeadline>
                <FormattedMessage
                  id={
                    areGiftCardsEnabled && areVouchersEnabled
                      ? 'payment.giftCardAndVoucher.headline'
                      : areGiftCardsEnabled
                      ? 'payment.giftCard.headline'
                      : 'payment.voucher.headline'
                  }
                />
              </SubHeadline>
            )}
            {areGiftCardsEnabled && <GiftCardList />}
            {areVouchersEnabled && <VoucherList />}

            <DynamicFieldArea
              area="payment"
              conditionValues={{
                paymentOptionKey: values.paymentMethod,
                carrierGroupKey: values.carrier.carrierGroupKey,
                shippingOptionKey: values.carrier.shippingOptionKey,
              }}
            />

            <PriceSummary
              className="price-summary__box"
              total={basketTotal}
              shipping={basketShipping}
              subtotal={basketSubtotal}
              giftCard={giftCardDiscount}
            />

            <Navigation
              nextStepLabel={<FormattedMessage id="payment.payNow" />}
              onPreviousStepClick={() => navigate('/shipping')}
              isLoading={isLoading}
              isDisabled={isNavigationDisabled(values)}
            />

            {DEBUG && <pre style={{ marginTop: 60 }}>{JSON.stringify(values, null, 2)}</pre>}
          </div>
        )}
      </Formik>
      {isPaymentModalOpen &&
        (
          [
            'computop_creditcard',
            'computop_creditcard_mastercard',
            'computop_creditcard_visa',
            'computop_maestro',
            'computop_bancontact',
          ] as (string | undefined)[]
        ).includes(selectedPaymentMethod?.paymentOptionKey) &&
        !!orderConfirmation?.redirect && (
          <ComputopPayment
            onError={() => {
              setPaymentModalOpen(false);
              setPollingEnabled(true);
            }}
          />
        )}
      {isPaymentModalOpen &&
        (['arvato_creditcard', 'arvato_ideal'] as (string | undefined)[]).includes(
          selectedPaymentMethod?.paymentOptionKey
        ) && (
          <ArvatoPayment
            onClose={() => {
              setPaymentModalOpen(false);
              setPollingEnabled(true);
            }}
          />
        )}
    </>
  );
};

export default PaymentStep;
